import { z } from "zod";
import { SYSTEM_ITEM_TYPES } from "@order-up/models";
import { BarWineItemTagSchema } from "../bar/BarWineList";
import { ComponentGroupSchema } from "./ComponentGroup";
import { ItemVariantSchema } from "./ItemVariant";
import MenuGroupReferenceSchema from "./MenuGroupReference";
import { ModifierGroupReferenceBaseSchema } from "./ModifierGroupReference";
import SizePriceSchema from "./SizePrice";
import {
  AccountingInfoSchema,
  AgeRestrictionSchema,
  AlcoholInfoSchema,
  AllergenSchema,
  AvailabilityWindowSchema,
  BundledItemSchema,
  ChannelPriceOverrideSchema,
  CostInfoSchema,
  CuisineTypeSchema,
  // Enums
  DietaryLabelSchema,
  DiscoveryInfoSchema,
  ExternalPlatformIdSchema,
  ItemTypeSchema,
  MediaAssetSchema,
  // Composite schemas
  NutritionalInfoSchema,
  PhysicalPropertiesSchema,
  PortionSizeSchema,
  PreparationInfoSchema,
  PreparationMethodSchema,
  PreparationTypeSchema,
  PricingStrategySchema,
  ProductCategorySchema,
  SalesChannelSchema,
  SeasonalAvailabilitySchema,
  SpicyLevelSchema,
  StockInfoSchema,
  TasteProfileSchema,
  TaxClassificationSchema,
  TemperatureCategorySchema,
  TranslationSchema,
} from "./types";

// Base object schema without refinements (for extend/omit operations)
export const ItemBaseSchema = z
  .object({
    // ============================================================================
    // CORE IDENTIFIERS
    // ============================================================================
    itemId: z.number().nullable().optional().readonly(),
    guid: z.string().nullable().optional().readonly(),
    number: z.number().nullable().optional().readonly(),

    // ============================================================================
    // DISPLAY INFORMATION
    // ============================================================================
    name: z
      .string()
      .min(1, { message: "Item name cannot be empty" })
      .readonly(),
    posName: z.string().nullable().optional().readonly(),
    posColor: z.string().nullable().optional().readonly(),
    posDescription: z.string().nullable().optional().readonly(),
    customerDescription: z.string().nullable().optional().readonly(),
    tagline: z.string().nullable().optional().readonly(),
    kitchenName: z.string().nullable().optional().readonly(),
    receiptName: z.string().nullable().optional().readonly(),
    kdsColor: z.string().nullable().optional().readonly(),
    position: z.number().int().nullable().optional().readonly(),

    // ============================================================================
    // MEDIA (Enhanced for multi-platform sync)
    // ============================================================================
    // Legacy: URL string or JSON stringified OptimizedImage object
    imageUrl: z
      .string()
      .refine(
        (val) => {
          if (!val) return true;
          try {
            new URL(val);
            return true;
          } catch {
            try {
              const parsed = JSON.parse(val);
              return (
                parsed && typeof parsed === "object" && "original" in parsed
              );
            } catch {
              return false;
            }
          }
        },
        { message: "Image must be a valid URL or OptimizedImage JSON" },
      )
      .nullable()
      .optional()
      .readonly(),
    // Production: Multiple images with metadata
    mediaAssets: z.array(MediaAssetSchema).optional().readonly(),
    // Legacy/alternate field name
    media: z.array(MediaAssetSchema).optional().readonly(),
    thumbnailUrl: z.string().nullable().optional().readonly(),

    // ============================================================================
    // PRODUCT IDENTIFICATION (for inventory & platform sync)
    // ============================================================================
    plu: z.string().nullable().optional().readonly(),
    sku: z.string().nullable().optional().readonly(),
    upc: z.string().nullable().optional().readonly(),
    ean: z.string().nullable().optional().readonly(),
    gtin: z.string().nullable().optional().readonly(),
    barcode: z.string().nullable().optional().readonly(),

    // ============================================================================
    // PRICING
    // ============================================================================
    pricingStrategy: PricingStrategySchema.default("base").readonly(),
    basePrice: z.number().min(0).nullable().optional().readonly(),
    corePrice: z.number().min(0).nullable().optional().readonly(),
    sizePrice: z.array(SizePriceSchema).default([]).readonly(),
    openPrice: z.boolean().default(false).readonly(),

    // SYSTEM ITEM — Open Food / Open Drink. Absent on every ordinary item.
    // `nullish` so a clear-the-field write is representable on the wire; the
    // model collapses null to undefined on the way in.
    systemItemType: z.enum(SYSTEM_ITEM_TYPES).nullish().readonly(),
    variants: z.array(ItemVariantSchema).default([]).readonly(),
    variantOptions: z.array(z.string()).optional(),
    deliveryMarkupPercent: z.number().nullable().optional().readonly(),
    minPrice: z.number().min(0).nullable().optional().readonly(),
    maxPrice: z.number().min(0).nullable().optional().readonly(),
    // Channel-specific pricing (e.g., delivery markup)
    channelPrices: z.array(ChannelPriceOverrideSchema).optional().readonly(),
    // Menu group price references for menu-specific pricing
    // Used when pricingStrategy is "menu_specific"
    menuGroupPrices: z.array(MenuGroupReferenceSchema).default([]).readonly(),

    // ============================================================================
    // CLASSIFICATION
    // ============================================================================
    itemType: ItemTypeSchema.optional().readonly(),
    productCategory: ProductCategorySchema.optional().readonly(),
    cuisineType: CuisineTypeSchema.optional().readonly(),
    preparationType: PreparationTypeSchema.optional().readonly(),
    tasteProfiles: z.array(TasteProfileSchema).optional().readonly(),
    spicyLevel: SpicyLevelSchema.optional().readonly(),
    countryOfOrigin: z.string().optional().readonly(),
    temperatureCategory: TemperatureCategorySchema.optional().readonly(),
    taxClassifications: z.array(TaxClassificationSchema).optional().readonly(),

    // ============================================================================
    // MODIFIERS & COMPOSITION
    // ============================================================================
    modifierGroups: z
      .array(ModifierGroupReferenceBaseSchema)
      .default([])
      .readonly(),
    // Bundled items for combos
    bundledItems: z.array(BundledItemSchema).optional().readonly(),
    isCombo: z.boolean().optional().readonly(),
    comboCorePrice: z.number().min(0).nullable().optional().readonly(),
    /**
     * Composite "choose N of these ITEMS" prompts. Present and NON-EMPTY makes
     * this a composite item (see `isCompositeItem`).
     *
     * NO `.default([])` — deliberately, and this is the same rule
     * `MENU_GROUP_SCHEDULING.md` records for `perDay`. The management UI writes
     * the PARSED output of this schema, so a default would stamp
     * `componentGroups: []` onto every existing item on its next save, and an
     * empty array is indistinguishable from "the operator removed the groups".
     */
    componentGroups: z.array(ComponentGroupSchema).optional().readonly(),

    // ============================================================================
    // NUTRITIONAL & DIETARY
    // ============================================================================
    calories: z.number().min(0).nullable().optional().readonly(), // Legacy field
    nutritionalInfo: NutritionalInfoSchema.optional().readonly(),
    dietaryLabels: z.array(DietaryLabelSchema).optional().readonly(),
    allergens: z.array(AllergenSchema).optional().readonly(),
    ingredients: z.array(z.string()).optional().readonly(),
    additives: z.array(z.string()).optional().readonly(),
    servesNumPeople: z.number().int().positive().optional().readonly(),
    portionSize: PortionSizeSchema.optional().readonly(),
    preparationMethods: z.array(PreparationMethodSchema).optional().readonly(),

    // ============================================================================
    // REGULATORY & COMPLIANCE
    // ============================================================================
    containsAlcohol: z.boolean().default(false).readonly(),
    alcoholInfo: AlcoholInfoSchema.optional().readonly(),
    ageRestriction: AgeRestrictionSchema.optional().readonly(),
    containsTobacco: z.boolean().optional().readonly(),
    containsCannabis: z.boolean().optional().readonly(),
    prop65Warning: z.string().optional().readonly(),

    // ============================================================================
    // PREPARATION
    // ============================================================================
    preparationInfo: PreparationInfoSchema.optional().readonly(),
    prepTimeMinutes: z.number().min(0).nullable().optional().readonly(),
    cookTimeMinutes: z.number().min(0).nullable().optional().readonly(),

    // ============================================================================
    // PHYSICAL PROPERTIES (for shipping/delivery)
    // ============================================================================
    physicalProperties: PhysicalPropertiesSchema.optional().readonly(),
    hasReusablePackaging: z.boolean().optional().readonly(),
    containerDeposit: z.number().min(0).nullable().optional().readonly(),

    // ============================================================================
    // STATION & KITCHEN
    // ============================================================================
    prepStationIds: z.array(z.number().int()).default([]).readonly(),
    courseId: z.number().int().nullable().optional().readonly(),
    salesCategoryId: z.number().int().nullable().optional().readonly(),
    // menuGroupIds removed - relationship owned by MenuGroup.items[]
    taxRateIds: z.array(z.number().int()).default([]).readonly(),

    // ============================================================================
    // TAX & ACCOUNTING
    // ============================================================================
    taxInclusion: z.boolean().default(false).readonly(),
    takeoutTaxException: z.boolean().default(false).readonly(),
    vatRatePercent: z.number().min(0).nullable().optional().readonly(),
    // Discounts and promo codes skip the item; comps still apply. Absent = discountable.
    excludeFromDiscounts: z.boolean().optional().readonly(),
    costInfo: CostInfoSchema.optional().readonly(),
    accountingInfo: AccountingInfoSchema.optional().readonly(),

    // ============================================================================
    // AVAILABILITY & STOCK
    // ============================================================================
    stockInfo: StockInfoSchema.optional().readonly(),
    isSuspended: z.boolean().optional().readonly(),
    suspendedUntil: z.date().nullable().optional().readonly(),
    alwaysAvailable: z.boolean().optional().readonly(),
    quantityRemaining: z.number().int().min(0).nullable().optional().readonly(),
    availabilityWindows: z
      .array(AvailabilityWindowSchema)
      .optional()
      .readonly(),
    seasonalAvailability: SeasonalAvailabilitySchema.optional().readonly(),

    // ============================================================================
    // CHANNEL VISIBILITY (Legacy booleans kept for backwards compatibility)
    // ============================================================================
    pos: z.boolean().default(true).readonly(),
    kiosk: z.boolean().default(true).readonly(),
    onlineOrdering: z.boolean().default(true).readonly(),
    customerInvoices: z.boolean().default(true).readonly(),
    onlineOrderingPartners: z.boolean().default(true).readonly(),
    // Production: Granular channel control
    enabledChannels: z.array(SalesChannelSchema).optional().readonly(),
    disabledChannels: z.array(SalesChannelSchema).optional().readonly(),

    // ============================================================================
    // DISCOVERY & MERCHANDISING
    // ============================================================================
    discoveryInfo: DiscoveryInfoSchema.optional().readonly(),

    // ============================================================================
    // MULTI-LANGUAGE SUPPORT
    // ============================================================================
    translations: z.array(TranslationSchema).optional().readonly(),

    // ============================================================================
    // PLATFORM SYNC
    // ============================================================================
    externalIds: z.array(ExternalPlatformIdSchema).optional().readonly(),
    externalData: z.string().optional().readonly(),
    lastSyncedAt: z.date().nullable().optional().readonly(),

    // ============================================================================
    // METADATA
    // ============================================================================
    orderingRules: z.undefined().optional().readonly(),
    tagIds: z.array(z.number().int()).default([]).readonly(),
    allergyCategoryIds: z.array(z.number().int()).default([]).readonly(),
    ingredientIds: z.array(z.number().int()).default([]).readonly(),
    // RecipeOps (Phase 14-R): non-authoritative pointer to the linked
    // operational recipe (recipe_items.guid). Must be inside .strict().
    recipeGuid: z.string().nullable().optional().readonly(),
    // RecipeOps (Phase 14-R, Decision 4): tenant-scoped allergen/ingredient
    // link to this restaurant's inventory_items (by GUID). Inside .strict().
    linkedInventoryGuids: z.array(z.string()).optional().readonly(),
    // Bar catalog link (`bar_catalog_items.guid`). Written by the API on
    // adoption; while set, posDescription + ingredientIds are catalog-owned.
    barCatalogEntryGuid: z.string().nullable().optional().readonly(),
    // The photo OrderUp last wrote on a generated mixed-drink item (design
    // 2026-10-05 Part 1). Server-written; declared because this schema is
    // .strict() and a merged-document validation would refuse the item.
    barImageSource: z.string().nullable().optional().readonly(),
    // A listed wine's bottle (`IItem.barWine`, design 2026-10-16) — what the
    // shared Serving options pour on THIS item. Server-written; declared
    // because this schema is .strict() and a merged-document validation
    // would otherwise refuse every manager save of a wine item.
    barWine: BarWineItemTagSchema.nullable().optional().readonly(),
    // The item's own price for an option on ONE menu (`IItem.menuGroupModifierPrices`)
    // — a wine's Happy Hour Glass. Server-written by the wine list today;
    // declared because this schema is .strict().
    menuGroupModifierPrices: z
      .array(
        z
          .object({
            menuGroupId: z.number().int(),
            modifierGroupId: z.number().int(),
            modifierId: z.number().int(),
            price: z.number().finite().min(0),
          })
          .strict(),
      )
      .nullable()
      .optional()
      .readonly(),
    // Ingredient availability (models/menus/ingredientAvailability.ts) —
    // SERVER-WRITE-ONLY, written by IngredientAvailabilityService. Declared
    // here because this schema is .strict(): a merged-document validation that
    // met an undeclared key would refuse every save of an affected item.
    ingredientStock: z
      .object({
        blocked: z.boolean(),
        missing: z.array(
          z.object({
            inventoryGuid: z.string(),
            name: z.string(),
            criticality: z.enum(["vital", "partial"]),
          }),
        ),
        notice: z.string().nullable(),
        updatedAt: z.string(),
      })
      .nullable()
      .optional()
      .readonly(),
    createdBy: z.string().nullable().optional().readonly(),
    updatedBy: z.string().nullable().optional().readonly(),
    isActive: z.boolean().optional().readonly(),
    isArchived: z.boolean().optional().readonly(),
    createdAt: z.date().nullable().optional().readonly(),
    updatedAt: z.date().nullable().optional().readonly(),
  })
  .strict();

// Schema with refinements for validation (built from base schema)
const ItemSchema = ItemBaseSchema.refine(
  (data) => {
    // Base pricing requires basePrice
    if (data.pricingStrategy === "base") {
      return (
        data.basePrice !== null &&
        data.basePrice !== undefined &&
        data.basePrice >= 0
      );
    }
    return true;
  },
  {
    message: "Base pricing strategy requires a valid base price",
    path: ["basePrice"],
  },
)
  .refine(
    (data) => {
      // Size pricing requires at least one size price
      if (data.pricingStrategy === "size") {
        return data.sizePrice.length > 0;
      }
      return true;
    },
    {
      message: "Size pricing strategy requires at least one size price",
      path: ["sizePrice"],
    },
  )
  .refine(
    (data) => {
      // Open pricing requires openPrice to be true
      if (data.pricingStrategy === "open") {
        return data.openPrice === true;
      }
      return true;
    },
    {
      message: "Open pricing strategy requires openPrice to be enabled",
      path: ["openPrice"],
    },
  )
  .refine(
    (data) => {
      // Variant pricing requires at least one variant
      if (data.pricingStrategy === "variant") {
        return data.variants.length > 0;
      }
      return true;
    },
    {
      message: "Variant pricing strategy requires at least one variant",
      path: ["variants"],
    },
  )
  .refine(
    (data) => {
      // Menu-specific pricing requires a base price as the default fallback
      if (data.pricingStrategy === "menu_specific") {
        return (
          data.basePrice !== null &&
          data.basePrice !== undefined &&
          data.basePrice >= 0
        );
      }
      return true;
    },
    {
      message:
        "Menu-specific pricing requires a base price as the default fallback",
      path: ["basePrice"],
    },
  );

export type ItemType = z.infer<typeof ItemSchema>;

export default ItemSchema;
